import { useEffect, useState } from "react";
import axios from "axios";
import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";
import { Heart, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Donate() {
  const [tiers, setTiers] = useState([]);
  const [funds, setFunds] = useState([]);
  const [selectedTier, setSelectedTier] = useState("double-chai");
  const [customAmount, setCustomAmount] = useState("");
  const [fund, setFund] = useState("general");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [dedication, setDedication] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/donations/tiers`).then((r) => {
      setTiers(r.data?.tiers || []);
      setFunds(r.data?.funds || []);
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        fund,
        donor_name: donorName || undefined,
        donor_email: donorEmail || undefined,
        dedication: dedication || undefined,
      };
      if (selectedTier === "custom") {
        const amt = parseFloat(customAmount);
        if (!amt || amt < 5) {
          toast.error("Please enter at least $5.00");
          setBusy(false);
          return;
        }
        payload.custom_amount = amt;
      } else {
        payload.tier_id = selectedTier;
      }
      const { data } = await axios.post(`${API}/donations/checkout`, payload);
      if (data?.url) window.location.href = data.url;
      else { toast.error("Could not start checkout."); setBusy(false); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Donation failed. Please try again.");
      setBusy(false);
    }
  };

  return (
    <>
      <SEOHead title="Donate" description="Support Temple Israel of Brevard with a tax-deductible donation. Give chai ($18), tikkun ($360), or any amount to keep our community vibrant." path="/donate" />
      <PageHero eyebrow="Tzedakah · Repair the World" title="Your generosity sustains our sacred work." subtitle="Every gift - whether chai or beyond - supports our sanctuary, our religious school, and our outreach." />

      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-5 lg:px-10">
          <form onSubmit={submit} className="card-soft p-7 md:p-10 space-y-8 bg-white" data-testid="donate-form">
            <div>
              <h2 className="font-display text-3xl text-[var(--blue-ink)] mb-1">Choose a gift</h2>
              <p className="text-sm text-[var(--muted)] mb-6">
                Suggested gifts based on the Hebrew tradition of <em>chai</em> (18 = life).
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="donate-tiers">
                {tiers.map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => setSelectedTier(t.id)}
                    data-testid={`donate-tier-${t.id}`}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${
                      selectedTier === t.id
                        ? "border-[var(--blue)] bg-[var(--blue)]/8"
                        : "border-[var(--line)] hover:border-[var(--blue)]/50 bg-white"
                    }`}
                  >
                    <div className="font-display text-2xl text-[var(--blue-ink)]">${t.amount.toFixed(0)}</div>
                    <div className="text-xs font-semibold text-[var(--blue-deep)] mt-1">{t.label}</div>
                    <div className="text-xs text-[var(--muted)] mt-1">{t.subtitle}</div>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setSelectedTier("custom")}
                  data-testid="donate-tier-custom"
                  className={`p-4 rounded-2xl border-2 text-left transition-all ${
                    selectedTier === "custom"
                      ? "border-[var(--blue)] bg-[var(--blue)]/8"
                      : "border-[var(--line)] hover:border-[var(--blue)]/50 bg-white"
                  }`}
                >
                  <div className="font-display text-2xl text-[var(--blue-ink)]">$ -</div>
                  <div className="text-xs font-semibold text-[var(--blue-deep)] mt-1">Custom</div>
                  <div className="text-xs text-[var(--muted)] mt-1">Your amount</div>
                </button>
              </div>

              {selectedTier === "custom" && (
                <div className="mt-5">
                  <label className="block text-sm font-medium text-[var(--ink)] mb-2">Custom amount (USD)</label>
                  <div className="flex items-center gap-2 max-w-xs">
                    <span className="text-2xl font-display text-[var(--blue-ink)]">$</span>
                    <input
                      type="number"
                      min="5"
                      max="50000"
                      step="0.01"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="flex-1 px-4 py-3 rounded-xl border border-[var(--line)] focus:outline-none focus:border-[var(--blue)]"
                      placeholder="100.00"
                      data-testid="donate-custom-amount"
                      required={selectedTier === "custom"}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Your name (optional)">
                <input type="text" value={donorName} onChange={(e) => setDonorName(e.target.value)} className="inputf" data-testid="donate-name" />
              </Field>
              <Field label="Email (for receipt)">
                <input type="email" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)} className="inputf" data-testid="donate-email" />
              </Field>
              <Field label="Designate to a fund">
                <select value={fund} onChange={(e) => setFund(e.target.value)} className="inputf bg-white" data-testid="donate-fund">
                  {funds.map((f) => (
                    <option key={f.id} value={f.id}>{f.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="In honor / In memory of (optional)">
                <input type="text" value={dedication} onChange={(e) => setDedication(e.target.value)} className="inputf" placeholder="e.g., In memory of…" data-testid="donate-dedication" />
              </Field>
            </div>

            <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="donate-submit">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <Heart size={18} />}
              {busy ? "Redirecting to secure checkout…" : "Continue to secure checkout"}
            </button>

            <p className="text-xs text-center text-[var(--muted)] flex items-center justify-center gap-2">
              <Lock size={12} /> Secure payments via Stripe. TIOB is a 501(c)(3); donations may be tax-deductible to the extent allowed by law.
            </p>
            <style>{`
              .inputf { width: 100%; padding: 0.85rem 1rem; border: 1px solid var(--line); border-radius: 0.75rem; outline: none; transition: border-color 200ms; background: white; }
              .inputf:focus { border-color: var(--blue); }
            `}</style>
          </form>
        </div>
      </section>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[var(--ink)] mb-2">{label}</span>
      {children}
    </label>
  );
}
